import React, { useEffect, useState, useRef } from 'react';
import Topbar from '../components/Topbar';
import { supabase } from '../lib/supabase';
import { useToast } from '../components/ui/Feedback';
import {
  Pencil, Trash2, Plus, Check, X, Eye, EyeOff,
  BookOpen, Calendar, Tag, Image, Type,
  Heading1, Heading2, List, Quote, Code,
  Upload, Loader,
} from 'lucide-react';
import { friendlyError } from "../lib/errorMessage";

const CATEGORIES = [
  'Treatment', 'Natural Hair', 'Protective Styles', 'Repair',
  'Scalp Care', 'Hair Growth', 'Styling', 'Product Reviews',
];

type Blog = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  cover_image: string;
  category: string;
  tags: string[];
  author_name: string;
  author_avatar: string;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  views_count: number;
};

type Block =
  | { type: 'paragraph'; content: string }
  | { type: 'heading1'; content: string }
  | { type: 'heading2'; content: string }
  | { type: 'quote'; content: string }
  | { type: 'code'; content: string }
  | { type: 'bulletList'; items: string[] }
  | { type: 'image'; url: string; caption: string }
  | { type: 'youtube'; url: string; caption: string };

const emptyForm = {
  title: '',
  slug: '',
  excerpt: '',
  cover_image: '',
  category: 'Natural Hair',
  tags: '',
  author_name: 'Zorax Team',
  author_avatar: '',
  is_published: false,
};

function toSlug(title: string) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function getYouTubeId(url: string) {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  return match ? match[1] : null;
}

function blocksToMarkdown(blocks: Block[]): string {
  return blocks.map((b) => {
    if (b.type === 'paragraph') return b.content;
    if (b.type === 'heading1') return `# ${b.content}`;
    if (b.type === 'heading2') return `## ${b.content}`;
    if (b.type === 'quote') return `> ${b.content}`;
    if (b.type === 'code') return `\`\`\`\n${b.content}\n\`\`\``;
    if (b.type === 'bulletList') return b.items.map((i) => `- ${i}`).join('\n');
    if (b.type === 'image') return `![${b.caption}](${b.url})${b.caption ? `\n*${b.caption}*` : ''}`;
    if (b.type === 'youtube') return `[youtube:${b.url}]${b.caption ? `\n*${b.caption}*` : ''}`;
    return '';
  }).join('\n\n');
}

function markdownToBlocks(md: string): Block[] {
  if (!md) return [{ type: 'paragraph', content: '' }];
  const segments = md.split(/\n\n+/);
  return segments.map((seg): Block => {
    seg = seg.trim();
    if (seg.startsWith('# ')) return { type: 'heading1', content: seg.slice(2) };
    if (seg.startsWith('## ')) return { type: 'heading2', content: seg.slice(3) };
    if (seg.startsWith('> ')) return { type: 'quote', content: seg.slice(2) };
    if (seg.startsWith('```')) return { type: 'code', content: seg.replace(/```\w*\n?/, '').replace(/```$/, '') };
    if (seg.startsWith('[youtube:')) {
      const url = seg.match(/\[youtube:(.*?)\]/)?.[1] || '';
      const caption = seg.match(/\*(.*?)\*/)?.[1] || '';
      return { type: 'youtube', url, caption };
    }
    if (seg.startsWith('![')) {
      const caption = seg.match(/!\[(.*?)\]/)?.[1] || '';
      const url = seg.match(/\]\((.*?)\)/)?.[1] || '';
      return { type: 'image', url, caption };
    }
    if (seg.split('\n').every((l) => l.startsWith('- '))) {
      return { type: 'bulletList', items: seg.split('\n').map((l) => l.slice(2)) };
    }
    return { type: 'paragraph', content: seg };
  });
}

// ── Block editor component ────────────────────────────────────────────────────
function BlockEditor({ blocks, onChange }: { blocks: Block[]; onChange: (b: Block[]) => void }) {
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
  // fileInputRef and activeUploadIdx were declared here and never read —
  // leftovers from a hidden-file-input upload flow that was replaced by the
  // per-block inputs below.

  const update = (idx: number, patch: Partial<Block>) => {
    onChange(blocks.map((b, i) => i === idx ? { ...b, ...patch } as Block : b));
  };

  const addBlock = (type: Block['type'], afterIdx?: number) => {
    const newBlock: Block =
      type === 'bulletList' ? { type, items: [''] } :
      type === 'image' ? { type, url: '', caption: '' } :
      type === 'youtube' ? { type, url: '', caption: '' } :
      { type, content: '' } as Block;

    const insertAt = afterIdx !== undefined ? afterIdx + 1 : blocks.length;
    const next = [...blocks];
    next.splice(insertAt, 0, newBlock);
    onChange(next);
  };

  const removeBlock = (idx: number) => {
    if (blocks.length === 1) return;
    onChange(blocks.filter((_, i) => i !== idx));
  };

  const moveBlock = (idx: number, dir: -1 | 1) => {
    const next = [...blocks];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange(next);
  };

  const handleImageUpload = async (file: File, idx: number) => {
    setUploadingIdx(idx);
    try {
      const ext = file.name.split('.').pop();
      const path = `blog/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('blog-images').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('blog-images').getPublicUrl(path);
      update(idx, { url: publicUrl } as Partial<Block>);
    } catch (err: unknown) {
      alert('Upload failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setUploadingIdx(null);
    }
  };

  // handleCoverUpload lived here and was never called — the cover image is
  // uploaded by the form's own handler further up. Removed rather than wired
  // in: two upload paths writing to the same bucket is how you end up with
  // orphaned objects.

  return (
    <div className="block-editor">
      {blocks.map((block, idx) => (
        <div key={idx} className="block-row">
          {/* Block controls */}
          <div className="block-controls">
            <button className="block-ctrl-btn" title="Move up" onClick={() => moveBlock(idx, -1)} disabled={idx === 0}>↑</button>
            <button className="block-ctrl-btn" title="Move down" onClick={() => moveBlock(idx, 1)} disabled={idx === blocks.length - 1}>↓</button>
            <button className="block-ctrl-btn block-ctrl-del" title="Remove block" onClick={() => removeBlock(idx)}>×</button>
          </div>

          {/* Block content */}
          <div className="block-content">
            {(block.type === 'paragraph') && (
              <textarea
                className="block-input block-paragraph"
                placeholder="Start writing..."
                value={block.content}
                rows={3}
                onChange={(e) => update(idx, { content: e.target.value })}
              />
            )}

            {block.type === 'heading1' && (
              <input
                className="block-input block-h1"
                placeholder="Heading 1"
                value={block.content}
                onChange={(e) => update(idx, { content: e.target.value })}
              />
            )}

            {block.type === 'heading2' && (
              <input
                className="block-input block-h2"
                placeholder="Heading 2"
                value={block.content}
                onChange={(e) => update(idx, { content: e.target.value })}
              />
            )}

            {block.type === 'quote' && (
              <textarea
                className="block-input block-quote"
                placeholder="Blockquote..."
                value={block.content}
                rows={2}
                onChange={(e) => update(idx, { content: e.target.value })}
              />
            )}

            {block.type === 'code' && (
              <textarea
                className="block-input block-code"
                placeholder="Code block..."
                value={block.content}
                rows={4}
                onChange={(e) => update(idx, { content: e.target.value })}
              />
            )}

            {block.type === 'bulletList' && (
              <div className="block-list">
                {block.items.map((item, li) => (
                  <div key={li} className="block-list-row">
                    <span className="list-dot">•</span>
                    <input
                      className="block-input block-list-input"
                      placeholder={`List item ${li + 1}`}
                      value={item}
                      onChange={(e) => {
                        const items = [...block.items];
                        items[li] = e.target.value;
                        update(idx, { items });
                      }}
                    />
                    <button className="list-remove-btn" onClick={() => {
                      const items = block.items.filter((_, i) => i !== li);
                      update(idx, { items: items.length ? items : [''] });
                    }}>×</button>
                  </div>
                ))}
                <button className="list-add-btn" onClick={() => update(idx, { items: [...block.items, ''] })}>
                  + Add item
                </button>
              </div>
            )}

            {block.type === 'image' && (
              <div className="block-image-wrap">
                {block.url ? (
                  <div className="block-image-preview">
                    <img src={block.url} alt={block.caption} />
                    <button className="image-remove" onClick={() => update(idx, { url: '' } as Partial<Block>)}>× Remove</button>
                  </div>
                ) : (
                  <div className="image-upload-zone">
                    {uploadingIdx === idx ? (
                      <div className="uploading-state"><Loader size={20} className="spin" /> Uploading...</div>
                    ) : (
                      <>
                        <Upload size={24} />
                        <p>Drop an image or click to upload</p>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(file, idx);
                          }}
                        />
                        <div className="or-divider">or paste a URL</div>
                        <input
                          className="block-input"
                          placeholder="https://images.unsplash.com/..."
                          style={{ marginTop: 8 }}
                          onBlur={(e) => { if (e.target.value) update(idx, { url: e.target.value } as Partial<Block>); }}
                        />
                      </>
                    )}
                  </div>
                )}
                <input
                  className="block-input caption-input"
                  placeholder="Image caption (optional)"
                  value={block.caption}
                  onChange={(e) => update(idx, { caption: e.target.value } as Partial<Block>)}
                />
              </div>
            )}

            {block.type === 'youtube' && (
              <div className="block-youtube-wrap">
                <div className="youtube-input-row">
                  <Image size={18} color="#FF0000" />
                  <input
                    className="block-input"
                    placeholder="Paste YouTube URL (e.g. https://youtube.com/watch?v=...)"
                    value={block.url}
                    onChange={(e) => update(idx, { url: e.target.value } as Partial<Block>)}
                    style={{ flex: 1 }}
                  />
                </div>
                {block.url && getYouTubeId(block.url) && (
                  <div className="youtube-preview">
                    <iframe
                      src={`https://www.youtube.com/embed/${getYouTubeId(block.url)}`}
                      title="YouTube preview"
                      frameBorder="0"
                      allowFullScreen
                      style={{ width: '100%', aspectRatio: '16/9', borderRadius: 8 }}
                    />
                  </div>
                )}
                {block.url && !getYouTubeId(block.url) && (
                  <div className="youtube-invalid">⚠ Invalid YouTube URL</div>
                )}
                <input
                  className="block-input caption-input"
                  placeholder="Video caption (optional)"
                  value={block.caption}
                  onChange={(e) => update(idx, { caption: e.target.value } as Partial<Block>)}
                />
              </div>
            )}
          </div>

          {/* Add block after */}
          <div className="add-block-row">
            {[
              { type: 'paragraph', icon: <Type size={12} />, label: 'Text' },
              { type: 'heading1', icon: <Heading1 size={12} />, label: 'H1' },
              { type: 'heading2', icon: <Heading2 size={12} />, label: 'H2' },
              { type: 'bulletList', icon: <List size={12} />, label: 'List' },
              { type: 'quote', icon: <Quote size={12} />, label: 'Quote' },
              { type: 'code', icon: <Code size={12} />, label: 'Code' },
              { type: 'image', icon: <Image size={12} />, label: 'Image' },
              { type: 'youtube', icon: <Image size={12} />, label: 'YouTube' },
            ].map(({ type, icon, label }) => (
              <button
                key={type}
                className="add-block-btn"
                onClick={() => addBlock(type as Block['type'], idx)}
                title={`Add ${label}`}
              >
                {icon} {label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Blogs page ───────────────────────────────────────────────────────────
export default function Blogs() {
  const toast = useToast();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [blocks, setBlocks] = useState<Block[]>([{ type: 'paragraph', content: '' }]);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { fetchBlogs(); }, []);

  async function fetchBlogs() {
    const { data } = await supabase
      .from('blogs')
      .select('*')
      .order('created_at', { ascending: false });
    setBlogs((data as Blog[]) ?? []);
    setLoading(false);
  }

  function openCreate() {
    setForm(emptyForm);
    setBlocks([{ type: 'paragraph', content: '' }]);
    setEditingBlog(null);
    setEditorOpen(true);
  }

  function openEdit(blog: Blog) {
    setEditingBlog(blog);
    setForm({
      title: blog.title ?? '',
      slug: blog.slug ?? '',
      excerpt: blog.excerpt ?? '',
      cover_image: blog.cover_image ?? '',
      category: blog.category ?? 'Natural Hair',
      tags: Array.isArray(blog.tags) ? blog.tags.join(', ') : '',
      author_name: blog.author_name ?? 'Zorax Team',
      author_avatar: blog.author_avatar ?? '',
      is_published: blog.is_published ?? false,
    });
    setBlocks(markdownToBlocks(blog.content));
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditingBlog(null);
  }

  const updateForm = (field: string, value: unknown) => {
    setForm((f) => {
      const updated = { ...f, [field]: value };
      if (field === 'title') updated.slug = toSlug(value as string);
      return updated;
    });
  };

  const handleCoverUpload = async (file: File) => {
    setCoverUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `covers/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('blog-images').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('blog-images').getPublicUrl(path);
      updateForm('cover_image', publicUrl);
    } catch (err: unknown) {
      alert('Upload failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setCoverUploading(false);
    }
  };

  async function handleSave() {
    if (!form.title.trim()) { alert('Title is required'); return; }
    const content = blocksToMarkdown(blocks);
    if (!content.trim()) { alert('Content is required'); return; }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || toSlug(form.title),
        excerpt: form.excerpt,
        content,
        cover_image: form.cover_image,
        category: form.category,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        author_name: form.author_name,
        author_avatar: form.author_avatar.trim() || null,
        is_published: form.is_published,
        published_at: form.is_published ? new Date().toISOString() : null,
      };

      if (editingBlog) {
        const { error } = await supabase.from('blogs').update(payload).eq('id', editingBlog.id);
        if (error) throw error;
        setBlogs((b) => b.map((x) => x.id === editingBlog.id ? { ...x, ...payload } as Blog : x));
      } else {
        const { data, error } = await supabase.from('blogs').insert(payload).select().single();
        if (error) throw error;
        setBlogs((b) => [data as Blog, ...b]);
      }
      closeEditor();
    } catch (err: unknown) {
      alert('Error: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  async function togglePublish(blog: Blog) {
    const newVal = !blog.is_published;
    const { error } = await supabase.from('blogs').update({
      is_published: newVal,
      published_at: newVal ? new Date().toISOString() : null,
    }).eq('id', blog.id);
    if (error) { toast.failure(friendlyError(error, "Something went wrong. Please try again.")); return; }
    toast.success(newVal ? 'Published' : 'Unpublished');
    setBlogs((b) => b.map((x) => x.id === blog.id ? { ...x, is_published: newVal } : x));
  }

  async function deleteBlog(id: string) {
    if (!window.confirm('Delete this blog post? This cannot be undone.')) return;
    const { error } = await supabase.from('blogs').delete().eq('id', id);
    if (error) { toast.failure(friendlyError(error, "Something went wrong. Please try again.")); return; }
    toast.success('Post deleted');
    setBlogs((b) => b.filter((x) => x.id !== id));
  }

  // ── Full-screen editor ──────────────────────────────────────────────────────
  if (editorOpen) {
    return (
      <div className="editor-shell">
        {/* Editor top bar */}
        <div className="editor-topbar">
          <button className="editor-back-btn" onClick={closeEditor}>
            <X size={16} /> Discard
          </button>
          <div className="editor-topbar-center">
            <span className="editor-topbar-title">
              {editingBlog ? 'Editing post' : 'New blog post'}
            </span>
          </div>
          <div className="editor-topbar-actions">
            <label className="publish-toggle">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => updateForm('is_published', e.target.checked)}
              />
              <span>{form.is_published ? '🟢 Published' : '⚪ Draft'}</span>
            </label>
            <button
              className="editor-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <><Loader size={14} className="spin" /> Saving...</> : <><Check size={14} /> {editingBlog ? 'Save Changes' : 'Publish Post'}</>}
            </button>
          </div>
        </div>

        <div className="editor-body">
          {/* Left: Writing area */}
          <div className="editor-main">

            {/* Cover image */}
            <div className="cover-section">
              {form.cover_image ? (
                <div className="cover-preview">
                  <img src={form.cover_image} alt="Cover" />
                  <div className="cover-actions">
                    <button onClick={() => coverInputRef.current?.click()} className="cover-action-btn">
                      <Upload size={14} /> Replace
                    </button>
                    <button onClick={() => updateForm('cover_image', '')} className="cover-action-btn cover-remove">
                      <X size={14} /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="cover-upload-zone"
                  onClick={() => coverInputRef.current?.click()}
                >
                  {coverUploading
                    ? <><Loader size={20} className="spin" /> Uploading cover image...</>
                    : <><Image size={20} /> Click to add a cover image</>}
                </div>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCoverUpload(f); }}
              />
            </div>

            {/* Title */}
            <input
              className="editor-title-input"
              placeholder="Post title..."
              value={form.title}
              onChange={(e) => updateForm('title', e.target.value)}
            />

            {/* Excerpt */}
            <textarea
              className="editor-excerpt-input"
              placeholder="Short excerpt shown on listing pages..."
              value={form.excerpt}
              rows={2}
              onChange={(e) => updateForm('excerpt', e.target.value)}
            />

            <div className="editor-divider" />

            {/* Block editor */}
            <BlockEditor blocks={blocks} onChange={setBlocks} />
          </div>

          {/* Right: Metadata sidebar */}
          <div className="editor-sidebar">
            <div className="sidebar-section">
              <div className="sidebar-section-title">Post Settings</div>

              <div className="sidebar-field">
                <label>Category</label>
                <select value={form.category} onChange={(e) => updateForm('category', e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="sidebar-field">
                <label>Author</label>
                <input
                  type="text"
                  value={form.author_name}
                  onChange={(e) => updateForm('author_name', e.target.value)}
                />
              </div>

              {/* The last editable blogs column that had no field. Blank falls
                  back to whatever the marketing site renders by default. */}
              <div className="sidebar-field">
                <label>Author avatar URL</label>
                <input
                  type="url"
                  placeholder="https://…"
                  value={form.author_avatar}
                  onChange={(e) => updateForm('author_avatar', e.target.value)}
                />
              </div>

              <div className="sidebar-field">
                <label>URL Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => updateForm('slug', e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
                <span className="field-hint">/blog/{form.slug || '...'}</span>
              </div>

              <div className="sidebar-field">
                <label>Tags <span className="field-hint-inline">(comma separated)</span></label>
                <input
                  type="text"
                  placeholder="4C Hair, Deep Conditioning"
                  value={form.tags}
                  onChange={(e) => updateForm('tags', e.target.value)}
                />
              </div>
            </div>

            <div className="sidebar-section">
              <div className="sidebar-section-title">Cover Image URL</div>
              <div className="sidebar-field">
                <input
                  type="text"
                  placeholder="Or paste an image URL..."
                  value={form.cover_image}
                  onChange={(e) => updateForm('cover_image', e.target.value)}
                />
              </div>
            </div>

            <div className="sidebar-section">
              <div className="sidebar-section-title">Block Reference</div>
              <div className="block-legend">
                {[
                  ['Text', 'Regular paragraph'],
                  ['H1 / H2', 'Section headings'],
                  ['List', 'Bullet points'],
                  ['Quote', 'Pull quote / callout'],
                  ['Code', 'Code block'],
                  ['Image', 'Upload or URL'],
                  ['YouTube', 'Embed a video'],
                ].map(([name, desc]) => (
                  <div key={name} className="legend-row">
                    <strong>{name}</strong>
                    <span>{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <EditorStyles />
      </div>
    );
  }

  // ── Blog list ───────────────────────────────────────────────────────────────
  return (
    <div>
      <Topbar title="Blogs" />
      <div style={{ padding: '24px 28px' }}>

        <div className="page-header">
          <div>
            <div className="page-title">Blog Posts</div>
            <div className="page-sub">Create and manage content for your Zorax blog</div>
          </div>
          <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={15} /> Create New Blog
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {blogs.map((blog) => (
            <div key={blog.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

                {blog.cover_image ? (
                  <img
                    src={blog.cover_image}
                    alt=""
                    style={{ width: 88, height: 66, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div style={{ width: 88, height: 66, borderRadius: 10, flexShrink: 0, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BookOpen size={24} color="var(--primary)" />
                  </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    {blog.category && <span className="badge badge-primary">{blog.category}</span>}
                    <span className={blog.is_published ? 'badge badge-success' : 'badge badge-warning'}>
                      {blog.is_published ? 'Published' : 'Draft'}
                    </span>
                    {blog.views_count > 0 && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>👁 {blog.views_count} views</span>
                    )}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', marginBottom: 4 }}>{blog.title}</div>
                  {blog.excerpt && (
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 }}>
                      {blog.excerpt.length > 160 ? blog.excerpt.slice(0, 160) + '...' : blog.excerpt}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Calendar size={11} />
                      {new Date(blog.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>By {blog.author_name}</span>
                    {blog.tags?.length > 0 && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Tag size={11} />
                        {blog.tags.slice(0, 3).join(', ')}{blog.tags.length > 3 ? ` +${blog.tags.length - 3}` : ''}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    className="btn btn-sm"
                    title={blog.is_published ? 'Unpublish' : 'Publish'}
                    style={{ background: blog.is_published ? '#FFF9EC' : '#F0FFF4', color: blog.is_published ? '#D69E2E' : '#38A169', border: 'none' }}
                    onClick={() => togglePublish(blog)}
                  >
                    {blog.is_published ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: 'none' }}
                    onClick={() => openEdit(blog)}
                  >
                    <Pencil size={13} />
                  </button>
                  <button className="btn btn-sm btn-danger" style={{ border: 'none' }} onClick={() => deleteBlog(blog.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {blogs.length === 0 && !loading && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 80 }}>
              <BookOpen size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div style={{ fontWeight: 600, marginBottom: 6 }}>No blog posts yet</div>
              <div style={{ fontSize: 13 }}>Click "Create New Blog" to write your first post</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Editor styles ─────────────────────────────────────────────────────────────
function EditorStyles() {
  return (
    <style>{`
      .editor-shell {
        display: flex;
        flex-direction: column;
        height: 100vh;
        background: #f8f8f8;
        overflow: hidden;
      }

      /* Topbar */
      .editor-topbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 20px;
        height: 56px;
        background: #fff;
        border-bottom: 1px solid #eee;
        flex-shrink: 0;
        gap: 16px;
        z-index: 10;
      }

      .editor-back-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        background: transparent;
        border: 1px solid #eee;
        padding: 6px 14px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        color: #666;
        cursor: pointer;
        transition: all 0.15s;
        white-space: nowrap;
      }
      .editor-back-btn:hover { background: #fafafa; color: #333; }

      .editor-topbar-center { flex: 1; text-align: center; }
      .editor-topbar-title { font-size: 13px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.05em; }

      .editor-topbar-actions { display: flex; align-items: center; gap: 10px; }

      .publish-toggle {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 600;
        color: #555;
        cursor: pointer;
        padding: 6px 12px;
        border: 1px solid #eee;
        border-radius: 8px;
        white-space: nowrap;
      }
      .publish-toggle input { width: 14px; height: 14px; accent-color: #38A169; }

      .editor-save-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        background: #1a1518;
        color: #fff;
        border: none;
        padding: 8px 20px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: background 0.15s;
        white-space: nowrap;
      }
      .editor-save-btn:hover:not(:disabled) { background: #333; }
      .editor-save-btn:disabled { opacity: 0.55; cursor: not-allowed; }

      /* Body */
      .editor-body {
        display: grid;
        grid-template-columns: 1fr 280px;
        flex: 1;
        overflow: hidden;
      }

      .editor-main {
        overflow-y: auto;
        padding: 48px 64px;
        background: #fff;
        border-right: 1px solid #eee;
      }

      /* Cover */
      .cover-section { margin-bottom: 28px; }

      .cover-upload-zone {
        width: 100%;
        height: 200px;
        border: 2px dashed #e0e0e0;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        cursor: pointer;
        color: #aaa;
        font-size: 14px;
        font-weight: 500;
        transition: border-color 0.2s, background 0.2s;
      }
      .cover-upload-zone:hover { border-color: #1a1518; background: #fafafa; color: #1a1518; }

      .cover-preview { position: relative; border-radius: 12px; overflow: hidden; }
      .cover-preview img { width: 100%; height: 220px; object-fit: cover; display: block; }
      .cover-actions {
        position: absolute;
        top: 10px;
        right: 10px;
        display: flex;
        gap: 6px;
      }
      .cover-action-btn {
        display: flex;
        align-items: center;
        gap: 4px;
        background: rgba(255,255,255,0.9);
        border: none;
        padding: 5px 10px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        backdrop-filter: blur(4px);
      }
      .cover-remove { color: #e53e3e; }

      /* Title / excerpt */
      .editor-title-input {
        width: 100%;
        font-size: 32px;
        font-weight: 800;
        color: #1a1518;
        border: none;
        outline: none;
        margin-bottom: 12px;
        font-family: Georgia, serif;
        background: transparent;
      }
      .editor-title-input::placeholder { color: #ddd; }

      .editor-excerpt-input {
        width: 100%;
        font-size: 16px;
        color: #666;
        border: none;
        outline: none;
        resize: none;
        font-family: inherit;
        line-height: 1.6;
        background: transparent;
      }
      .editor-excerpt-input::placeholder { color: #ddd; }

      .editor-divider { height: 1px; background: #f0f0f0; margin: 24px 0; }

      /* Block editor */
      .block-editor { display: flex; flex-direction: column; }

      .block-row {
        position: relative;
        display: flex;
        gap: 0;
        flex-direction: column;
        margin-bottom: 4px;
      }

      .block-row:hover .block-controls { opacity: 1; }

      .block-controls {
        display: flex;
        gap: 2px;
        opacity: 0;
        transition: opacity 0.15s;
        margin-bottom: 4px;
      }

      .block-ctrl-btn {
        background: #f5f5f5;
        border: none;
        width: 24px;
        height: 24px;
        border-radius: 5px;
        font-size: 12px;
        cursor: pointer;
        color: #888;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.1s;
      }
      .block-ctrl-btn:hover { background: #eee; color: #333; }
      .block-ctrl-btn:disabled { opacity: 0.3; cursor: not-allowed; }
      .block-ctrl-del:hover { background: #fee; color: #e53e3e; }

      .block-content { width: 100%; }

      .block-input {
        width: 100%;
        border: 1px solid transparent;
        background: transparent;
        outline: none;
        font-family: inherit;
        box-sizing: border-box;
        transition: border-color 0.15s, background 0.15s;
        border-radius: 6px;
        padding: 8px 10px;
      }
      .block-input:focus { border-color: #e8e0d8; background: #fafafa; }

      .block-paragraph { font-size: 15px; line-height: 1.8; color: #333; resize: vertical; min-height: 60px; }
      .block-h1 { font-size: 26px; font-weight: 800; color: #1a1518; font-family: Georgia, serif; }
      .block-h2 { font-size: 20px; font-weight: 700; color: #1a1518; font-family: Georgia, serif; }
      .block-quote {
        font-size: 16px;
        font-style: italic;
        color: #555;
        border-left: 3px solid #9c8065;
        padding-left: 16px !important;
        resize: vertical;
        min-height: 48px;
      }
      .block-code {
        font-family: monospace;
        font-size: 13px;
        background: #1a1518 !important;
        color: #f1e7dc !important;
        border-color: transparent !important;
        border-radius: 8px !important;
        resize: vertical;
        min-height: 80px;
        line-height: 1.6;
      }

      /* List */
      .block-list { display: flex; flex-direction: column; gap: 4px; }
      .block-list-row { display: flex; align-items: center; gap: 8px; }
      .list-dot { color: #9c8065; font-size: 18px; flex-shrink: 0; }
      .block-list-input { flex: 1; font-size: 15px; }
      .list-remove-btn { background: none; border: none; cursor: pointer; color: #ccc; font-size: 16px; padding: 4px; }
      .list-remove-btn:hover { color: #e53e3e; }
      .list-add-btn { background: none; border: 1px dashed #ddd; border-radius: 6px; padding: 6px 12px; font-size: 13px; color: #888; cursor: pointer; margin-top: 4px; align-self: flex-start; }
      .list-add-btn:hover { border-color: #9c8065; color: #9c8065; }

      /* Image block */
      .block-image-wrap { display: flex; flex-direction: column; gap: 8px; }
      .block-image-preview { position: relative; border-radius: 10px; overflow: hidden; }
      .block-image-preview img { width: 100%; max-height: 400px; object-fit: cover; display: block; }
      .image-remove { position: absolute; top: 8px; right: 8px; background: rgba(255,255,255,0.9); border: none; padding: 4px 10px; border-radius: 6px; font-size: 12px; cursor: pointer; font-weight: 600; color: #e53e3e; }

      .image-upload-zone {
        position: relative;
        border: 2px dashed #e0e0e0;
        border-radius: 10px;
        padding: 32px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        color: #aaa;
        font-size: 14px;
        cursor: pointer;
        text-align: center;
        transition: border-color 0.2s;
      }
      .image-upload-zone:hover { border-color: #9c8065; color: #9c8065; }
      .or-divider { font-size: 12px; color: #ccc; margin: 4px 0; }
      .uploading-state { display: flex; align-items: center; gap: 8px; color: #9c8065; }

      /* YouTube block */
      .block-youtube-wrap { display: flex; flex-direction: column; gap: 8px; }
      .youtube-input-row { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border: 1px solid #eee; border-radius: 8px; background: #fafafa; }
      .youtube-preview { border-radius: 10px; overflow: hidden; margin-top: 4px; }
      .youtube-invalid { font-size: 12px; color: #e53e3e; padding: 4px 10px; }

      .caption-input { font-size: 13px; color: #888; font-style: italic; }

      /* Add block row */
      .add-block-row {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
        padding: 6px 0;
        opacity: 0;
        transition: opacity 0.15s;
      }
      .block-row:hover .add-block-row { opacity: 1; }

      .add-block-btn {
        display: flex;
        align-items: center;
        gap: 4px;
        background: #f5f5f5;
        border: none;
        padding: 4px 10px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 600;
        color: #888;
        cursor: pointer;
        transition: all 0.15s;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .add-block-btn:hover { background: #1a1518; color: #fff; }

      /* Sidebar */
      .editor-sidebar {
        overflow-y: auto;
        padding: 20px 16px;
        background: #fafafa;
        display: flex;
        flex-direction: column;
        gap: 0;
      }

      .sidebar-section {
        border-bottom: 1px solid #eee;
        padding-bottom: 16px;
        margin-bottom: 16px;
      }

      .sidebar-section-title {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #aaa;
        margin-bottom: 12px;
      }

      .sidebar-field {
        display: flex;
        flex-direction: column;
        gap: 5px;
        margin-bottom: 12px;
      }

      .sidebar-field label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #666;
      }

      .sidebar-field input,
      .sidebar-field select,
      .sidebar-field textarea {
        padding: 8px 10px;
        border: 1px solid #e8e0d8;
        border-radius: 6px;
        font-size: 13px;
        color: #1a1518;
        background: #fff;
        outline: none;
        font-family: inherit;
        transition: border-color 0.2s;
      }
      .sidebar-field input:focus,
      .sidebar-field select:focus { border-color: #1a1518; }

      .field-hint { font-size: 11px; color: #aaa; font-family: monospace; }
      .field-hint-inline { font-weight: 400; color: #bbb; text-transform: none; }

      /* Block legend */
      .block-legend { display: flex; flex-direction: column; gap: 6px; }
      .legend-row { display: flex; justify-content: space-between; font-size: 12px; }
      .legend-row strong { color: #555; }
      .legend-row span { color: #aaa; }

      /* Spin animation */
      .spin { animation: spin 0.7s linear infinite; }
      @keyframes spin { to { transform: rotate(360deg); } }

      @media (max-width: 900px) {
        .editor-body { grid-template-columns: 1fr; }
        .editor-sidebar { display: none; }
        .editor-main { padding: 32px 24px; }
      }
    `}</style>
  );
}